import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { useForm, Controller } from "react-hook-form";
import PhoneInput, { isValidPhoneNumber } from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import PropTypes from "prop-types";
import ReCAPTCHA from "react-google-recaptcha";
import { getConstants } from "./langUtils";
import { parseHTML, STRAPI_ASSETS_BASE_PATH } from "./utils";

const successIcon = `${STRAPI_ASSETS_BASE_PATH}success_b641d3796d.png`;
function SendMessageForm({
  dialogVisible,
  setDialogVisible,
  assignPartnerId,
  assignCenterId,
  selectedPlace,
  locale,
  BASE_URL,
  setLoading,
  recaptcha_sitemap_key,
  isDialog = true,
  entity
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    control,
  } = useForm({
    mode: "onChange",
  });
  const [emailSuccessRes, setEmailSuccessRes] = useState(false);
  const [errorDialogVisible, setErrorDialogVisible] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState("");
  const [recaptchaV3Token, setRecaptchaV3Token] = useState(null);
  const recaptchaRef = useRef(null);
  const nameRegx = /^(?![\s.]+$)[a-zA-Z\s]*$/;
  const emailRegx = /^[^@ ]+@[^@ ]+\.[^@ .]{2,}$/;

  useEffect(() => {
    const executeRecaptcha = () => {
      if (recaptchaRef.current) {
        recaptchaRef.current.executeAsync().then((token) => {
          setRecaptchaV3Token(token);
        });
      } else {
        setTimeout(executeRecaptcha, 500);
      }
    };

    executeRecaptcha();
  }, []);

  useEffect(() => {
    let interval;

    if (dialogVisible) {
      setTimeout(() => {
        if (recaptchaRef.current) {
          recaptchaRef.current.executeAsync().then(setRecaptchaV3Token);
        } else {
          setTimeout(() => {
            if (recaptchaRef.current) {
              recaptchaRef.current.executeAsync().then(setRecaptchaV3Token);
            }
          }, 200);
        }
      }, 100);

      interval = setInterval(() => {
        if (recaptchaRef.current) {
          recaptchaRef.current.executeAsync().then(setRecaptchaV3Token);
        }
      }, 90000);
    } else {
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      setRecaptchaV3Token(null);
    }

    return () => clearInterval(interval);
  }, [dialogVisible]);

  const onSubmit = async (data) => {
    let mobileNumber = data?.mobile;

    if (mobileNumber) {
      const reduceMobilenumber = mobileNumber?.split("-");
      mobileNumber =
        reduceMobilenumber.length && reduceMobilenumber[1]
          ? `${reduceMobilenumber[0]}${reduceMobilenumber[1]}`
          : mobileNumber;
    }

    if (mobileNumber && !mobileNumber.startsWith("+")) {
      mobileNumber = `+${mobileNumber}`;
    }

    if (!recaptchaV3Token) {
      setErrorDialogMessage(getConstants(locale)?.Re_captcha_verification);
      setErrorDialogVisible(true);
      return;
    }

    setLoading(true);
    const contactAPI = `${BASE_URL}/contact-us`;
    const requestBody = {
      contactType: "ENQUIRY",
      name: data.name,
      email: data.email,
      mobile: mobileNumber || "",
      message: data?.message || "",
      reCaptcha: recaptchaV3Token,
      assignedPartner: entity === "trainers" ? assignPartnerId : null,
      meditationCenter: entity === "centers" ?  assignCenterId : null,
      formattedAddress: selectedPlace?.formatted_address || "",
      placeId: selectedPlace?.place_id || "",
    };
    try {
      const response = await fetch(contactAPI, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        setEmailSuccessRes(true);
        reset();
        setRecaptchaV3Token(null);
        if (setDialogVisible) {
          setDialogVisible(false);
        }
      } else {
        const errorData = await response.json();
        setErrorDialogMessage(
          errorData?.message || "Error submitting the form. Please try again."
        );
        setErrorDialogVisible(true);
      }
    } catch (error) {
      setErrorDialogMessage(
        error?.message ||
          "An unexpected error occurred. Please try again later."
      );
      setErrorDialogVisible(true);
    } finally {
      setLoading(false);
    }
  };

  const formForSendingMessage = (
    <div>
      <h4>{getConstants(locale)?.SEND}</h4>
      <p>{getConstants(locale)?.FORMFIELDS?.FORMMESSAGE}</p>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-4">
          <label htmlFor="name" className="form-label">
            {getConstants(locale)?.FORMFIELDS?.NAME}*
            <input
              type="text"
              id="name"
              placeholder={getConstants(locale)?.FORMFIELDS?.PLACEHOLDER?.NAME}
              className="form-control"
              aria-describedby="emailHelp"
              aria-invalid={errors.name ? "true" : "false"}
              {...register("name", {
                required:
                  getConstants(locale)?.FORMFIELDS?.ERRORMSGS?.NAMEEMPTY,
                pattern: {
                  value: nameRegx,
                  message: getConstants(locale)?.FORMFIELDS?.ERRORMSGS?.NAME,
                },
              })}
            />
          </label>
          {errors.name && (
            <div role="alert" className="trainer-form-input-error">
              {errors.name.message}
            </div>
          )}
        </div>
        <div className="mb-4">
          <label htmlFor="email" className="form-label">
            {getConstants(locale)?.FORMFIELDS?.EMAIL}*
            <input
              type="email"
              id="email"
              placeholder={getConstants(locale)?.FORMFIELDS?.PLACEHOLDER?.EMAIL}
              className="form-control"
              aria-invalid={errors.email ? "true" : "false"}
              {...register("email", {
                required: getConstants(locale)?.FORMFIELDS?.ERRORMSGS?.EMAIL,
                pattern: {
                  value: emailRegx,
                  message:
                    getConstants(locale)?.FORMFIELDS?.ERRORMSGS?.EMAILINVALID,
                },
              })}
            />
          </label>
          {errors.email && (
            <div role="alert" className="trainer-form-input-error">
              {errors.email.message}
            </div>
          )}
        </div>
        <div className="mb-4">
          <span htmlFor="phoneInput" className="form-label">
            {getConstants(locale)?.FORMFIELDS?.PHONE}
          </span>
          <Controller
            name="mobile"
            control={control}
            defaultValue=""
            rules={{
              validate: (value) => {
                if (!value) return true;
                if (value.startsWith("91")) {
                  if (value.length !== 12)
                    return getConstants(locale)?.FORMFIELDS?.ERRORMSGS?.PHONE;
                } else if (
                  typeof isValidPhoneNumber === "function" &&
                  !isValidPhoneNumber(value)
                )
                  return getConstants(locale)?.FORMFIELDS?.ERRORMSGS?.PHONE;
                return true;
              },
            }}
            render={({ field }) => (
              <PhoneInput
                {...field}
                country="in"
                id="phoneInput"
                placeholder={
                  getConstants(locale)?.FORMFIELDS?.PLACEHOLDER?.PHONE
                }
                aria-invalid={errors.mobile ? "true" : "false"}
                value={field.value}
                onChange={field.onChange}
                name="mobile"
              />
            )}
          />
          {errors.mobile && (
            <div role="alert" className="trainer-form-input-error">
              {errors.mobile.message}
            </div>
          )}
        </div>
        <div className="mb-4">
          <label htmlFor="location" className="form-label">
            {getConstants(locale)?.LOCATION}
            <input
              type="text"
              id="location"
              className="form-control"
              placeholder={
                getConstants(locale)?.FORMFIELDS?.PLACEHOLDER?.LOCATION
              }
              {...register("location")}
            />
          </label>
        </div>
        <div className="mb-4">
          <label htmlFor="message" className="form-label">
            {getConstants(locale)?.FORMFIELDS?.MESSAGE}
            <textarea
              id="message"
              className="form-control"
              placeholder={
                getConstants(locale)?.FORMFIELDS?.PLACEHOLDER?.MESSAGE
              }
              rows="3"
              {...register("message")}
            />
          </label>
        </div>
        <div className="mb-4 form-check">
          <input
            type="checkbox"
            id="terms"
            className="form-check-input"
            {...register("terms", {
              required: getConstants(locale)?.FORMFIELDS?.ERRORMSGS?.DISCLAIMER,
            })}
          />
          <span
            className="form-check-label"
            dangerouslySetInnerHTML={parseHTML(
              getConstants(locale)?.FORMFIELDS?.DISCLAIMER
            )}
          />
          {errors.terms && (
            <div role="alert" className="trainer-form-input-error">
              {errors.terms.message}
            </div>
          )}
        </div>
        <div className="mb-4">
          <ReCAPTCHA
            sitekey={recaptcha_sitemap_key}
            size="invisible"
            ref={recaptchaRef}
            onChange={(token) => setRecaptchaV3Token(token)}
          />
        </div>
        <div className="mb-4">
          <button type="submit" className="submit-btn my-2 mx-1">
            {getConstants(locale)?.SUBMIT}
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <>
      {isDialog ? (
        <Dialog
          visible={dialogVisible}
          headerClassName="custom-dialog-header"
          contentClassName="custom-scrollbar"
          onHide={() => {
            setDialogVisible(false);
            reset();
          }}
          modal
        >
          {formForSendingMessage}
        </Dialog>
      ) : (
        formForSendingMessage
      )}
      <Dialog
        className="dialog-wrapper"
        visible={emailSuccessRes}
        onHide={() => setEmailSuccessRes(false)}
        modal
      >
        <div className="email-success">
          <img
            src={successIcon}
            alt="user_icon"
            priority={false}
            style={{ width: "60px", height: "54px" }}
          />
          <p>{getConstants(locale)?.Heartspot_form_message}</p>
        </div>
      </Dialog>
      <Dialog
        visible={errorDialogVisible}
        onHide={() => setErrorDialogVisible(false)}
        modal
      >
        <div className="error-message">
          <p>{errorDialogMessage}</p>
        </div>
      </Dialog>
    </>
  );
}

SendMessageForm.propTypes = {
  dialogVisible: PropTypes.bool.isRequired,
  setDialogVisible: PropTypes.func.isRequired,
  assignPartnerId: PropTypes.string.isRequired,
  selectedPlace: PropTypes.shape({
    formatted_address: PropTypes.string,
    place_id: PropTypes.string,
  }).isRequired,
  executeRecaptcha: PropTypes.func.isRequired,
  BASE_URL: PropTypes.string.isRequired,
  setLoading: PropTypes.func.isRequired,
  recaptcha_sitemap_key: PropTypes.string.isRequired,
  loading: PropTypes.bool.isRequired,
  isDialog: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
};

export default SendMessageForm;
